package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.usecase.start.FirstPublishTime
import com.evoapps.evoshift.domain.usecase.start.ObserveStartFactsUseCase
import com.evoapps.evoshift.domain.usecase.start.StartPath
import com.evoapps.evoshift.domain.usecase.start.StartPathProgress
import com.evoapps.evoshift.domain.usecase.start.StartPathProgressUseCase
import com.evoapps.evoshift.domain.usecase.start.StartStep
import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.settings.LocalSettings
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Instant

/**
 * SH-219: the start path card. [path] null: no card (not the owner, dismissed, a workplace set up
 * before the path, or not read yet). [message]: why a skip or a dismissal was not saved.
 */
data class StartPathUiState(
    val workplaceId: String? = null,
    val path: StartPath? = null,
    val message: UiText? = null
)

/**
 * SH-219: a new owner's start path at the current workplace ("first roster in ten minutes"): each
 * step ticked from the data ([ObserveStartFactsUseCase], read again on [refresh] when the owner
 * comes back from a step), skipped or resumed, the card dismissed once nothing is left to do. The
 * owner's choices are kept per workplace ([StartPathProgressUseCase]); the first publication on the
 * path sends the anonymous "time to first publish" metric once.
 *
 * Shown to the workplace's owner while the plan runs (billing and changes), as SH-170's first
 * steps were; their card hidden on this device ([LEGACY_DISMISSED_PREFIX]) stays hidden.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class StartPathViewModel(
    getCurrentUserUseCase: GetCurrentUserUseCase,
    sessionManager: WorkplaceSessionManager,
    private val observeFacts: ObserveStartFactsUseCase,
    private val store: StartPathProgressUseCase,
    private val settings: LocalSettings,
    plans: ObservePlanActiveUseCase? = null,
    private val now: () -> Instant = { AppClock.now() }
) : ViewModel() {
    private val _uiState = MutableStateFlow(StartPathUiState())
    val uiState = _uiState.asStateFlow()
    private val refreshes = MutableStateFlow(0)

    /** The shown workplace's choices; [readOk]: they were read, so writing them back loses nothing. */
    private class Shown(val workplaceId: String, val progress: MutableStateFlow<StartPathProgress>, val readOk: Boolean)

    private var shown: Shown? = null

    init {
        viewModelScope.launch {
            val user = getCurrentUserUseCase()
            sessionManager.currentWorkplaceId.flatMapLatest { workplaceId ->
                if (workplaceId == null) flowOf(null to emptySet())
                else capabilitiesAt(user, plans, workplaceId).map { workplaceId to it }
            }.distinctUntilChanged().flatMapLatest { (workplaceId, caps) ->
                val owner = workplaceId != null && Capability.BILLING in caps && Capability.MAKE_CHANGES in caps
                if (!owner) {
                    shown = null
                    flowOf(StartPathUiState(workplaceId))
                } else {
                    pathOf(workplaceId!!)
                }
            }.collect { state -> _uiState.update { state.copy(message = it.message) } }
        }
    }

    private suspend fun pathOf(workplaceId: String) = run {
        val read = store.load(workplaceId)
        val legacyDismissed = runCatching { settings.getString(LEGACY_DISMISSED_PREFIX + workplaceId) == "true" }.getOrDefault(false)
        val progress = read.getOrNull() ?: StartPathProgress()
        val current = Shown(
            workplaceId,
            MutableStateFlow(if (legacyDismissed) progress.copy(dismissed = true) else progress),
            readOk = read.isSuccess
        )
        shown = current
        combine(refreshes.flatMapLatest { observeFacts(workplaceId) }, current.progress) { facts, p -> StartPath(facts, p) }
            .onEach { path -> record(current, path) }
            .map { path -> StartPathUiState(workplaceId, path.takeIf { it.shows }) }
            .catch { emit(StartPathUiState(workplaceId)) }
    }

    /**
     * The path starts when the card is first shown with something to do (the metric's start); the
     * first month published on it sends the metric once, by its bucket only.
     */
    private fun record(current: Shown, path: StartPath) {
        if (!current.readOk) return
        val progress = path.progress
        if (progress.dismissed) return
        val at = now()
        when {
            progress.startedAt == null && !path.allDone -> keep(current, progress.copy(startedAt = at.toString()))
            progress.startedAt != null && progress.firstPublishedAt == null && path.facts.hasPublished -> {
                val started = runCatching { Instant.parse(progress.startedAt!!) }.getOrNull()
                if (started != null) AppTelemetry.log(AnalyticsEvent.FirstRosterPublished(FirstPublishTime.bucket(at - started)))
                keep(current, progress.copy(firstPublishedAt = at.toString()))
            }
        }
    }

    /** The bookkeeping above: written quietly, never rolled back (it is not the owner's choice). */
    private fun keep(current: Shown, progress: StartPathProgress) {
        current.progress.value = progress
        viewModelScope.launch { store.save(current.workplaceId, progress) }
    }

    /** "Skip": the step is set aside (its data still ticks it when done later). */
    fun skip(step: StartStep) = change { it.copy(skipped = it.skipped + step) }

    /** "Resume": a skipped step is to do again. */
    fun resume(step: StartStep) = change { it.copy(skipped = it.skipped - step) }

    /** "Hide": the card goes for good, once every step is done or skipped. */
    fun dismiss() {
        if (_uiState.value.path?.canDismiss != true) return
        change { it.copy(dismissed = true) }
    }

    /** Back from a step's screen: the data is read again. */
    fun refresh() {
        refreshes.value++
    }

    fun messageShown() = _uiState.update { it.copy(message = null) }

    /** The owner's choice shown at once and saved; a failed save puts it back and says why. */
    private fun change(transform: (StartPathProgress) -> StartPathProgress) {
        val current = shown ?: return
        val before = current.progress.value
        val after = transform(before)
        if (after == before) return
        current.progress.value = after
        viewModelScope.launch {
            store.save(current.workplaceId, after).onFailure { e ->
                if (current.progress.value == after) current.progress.value = before
                _uiState.update { it.copy(message = e.toUiText()) }
            }
        }
    }

    companion object {
        /** SH-170's "Pierwsze kroki" hidden on this device (per workplace). */
        const val LEGACY_DISMISSED_PREFIX = "first_steps_dismissed_"
    }
}
